/**
 * The paper a file is laid out on: one declaration (`export.paper` on a list or a report), read by
 * every format that has pages — the print view's `@page`, the Word document's section, the
 * workbook's page setup — so the three cannot disagree about the sheet they print on.
 *
 * `size` is a named sheet or `custom` with both sides in millimetres; `orientation` is `portrait`,
 * `landscape`, or `auto`: landscape past six columns, the rule every export followed before paper
 * could be declared, so a screen that declares nothing prints exactly as it did. For a custom sheet
 * `auto` keeps the sides as declared; portrait and landscape turn it so its long side runs down or
 * across.
 */
export const PAPER_SIZES = ['a4', 'a3', 'a5', 'letter', 'custom'];
export const PAPER_ORIENTATIONS = ['portrait', 'landscape', 'auto'];
/** A custom sheet's sides: no smaller than a label, no larger than a plan printer's roll is wide. */
export const PAPER_MM_MIN = 50;
export const PAPER_MM_MAX = 1200;
/** Past this many columns `auto` turns the sheet: today's rule, unchanged. */
export const WIDE_COLUMNS = 6;
/** The named sheets, portrait, in millimetres; the CSS name; and the workbook's paper code. */
const NAMED = {
    a4: { w: 210, h: 297, css: 'A4', xlsx: 9 },
    a3: { w: 297, h: 420, css: 'A3', xlsx: 8 },
    a5: { w: 148, h: 210, css: 'A5', xlsx: 11 },
    letter: { w: 215.9, h: 279.4, css: 'letter', xlsx: 1 },
};
/** What a declaration does wrong, one sentence each; empty when it is right. */
export function paperProblems(paper) {
    if (paper === undefined)
        return [];
    if (!paper || typeof paper !== 'object' || Array.isArray(paper))
        return ['the export paper is not a paper declaration'];
    const p = paper;
    const out = [];
    const size = p['size'] ?? 'a4';
    if (!PAPER_SIZES.includes(size))
        out.push(`paper size "${String(size)}" is not one of ${PAPER_SIZES.join(', ')}`);
    if (p['orientation'] !== undefined && !PAPER_ORIENTATIONS.includes(p['orientation'])) {
        out.push(`paper orientation "${String(p['orientation'])}" is not one of ${PAPER_ORIENTATIONS.join(', ')}`);
    }
    const sides = ['width_mm', 'height_mm'].filter((k) => p[k] !== undefined);
    if (size === 'custom') {
        for (const k of ['width_mm', 'height_mm']) {
            const v = p[k];
            if (typeof v !== 'number' || !Number.isFinite(v) || v < PAPER_MM_MIN || v > PAPER_MM_MAX) {
                out.push(`a custom paper needs ${k} between ${PAPER_MM_MIN} and ${PAPER_MM_MAX} millimetres`);
            }
        }
    }
    else if (sides.length) {
        out.push(`paper size "${String(size)}" has its own sides; ${sides.join(' and ')} belong to a custom paper`);
    }
    return out;
}
/** The sheet a file of `columns` columns is laid out on. A declaration that is not right is read as none. */
export function paperOf(paper, columns) {
    const ok = paperProblems(paper).length === 0;
    const p = ok && paper ? paper : {};
    const orientation = p.orientation ?? 'auto';
    if (p.size === 'custom') {
        const w = p.width_mm;
        const h = p.height_mm;
        const landscape = orientation === 'auto' ? w > h : orientation === 'landscape';
        const [long, short] = w >= h ? [w, h] : [h, w];
        return orientation === 'auto'
            ? { width_mm: w, height_mm: h, landscape, css: null, xlsx: null }
            : { width_mm: landscape ? long : short, height_mm: landscape ? short : long, landscape, css: null, xlsx: null };
    }
    const named = NAMED[(p.size ?? 'a4')];
    const landscape = orientation === 'auto' ? columns > WIDE_COLUMNS : orientation === 'landscape';
    return { width_mm: landscape ? named.h : named.w, height_mm: landscape ? named.w : named.h, landscape, css: named.css, xlsx: named.xlsx };
}
/** A number of millimetres as CSS writes it: no trailing zeros, Western digits. */
function mm(n) {
    return `${Number(n.toFixed(2))}mm`;
}
/** The `size` of the print view's `@page`: `A4 portrait` for a named sheet, `300mm 200mm` for a custom one. */
export function pageSize(paper, columns) {
    const r = paperOf(paper, columns);
    return r.css ? `${r.css} ${r.landscape ? 'landscape' : 'portrait'}` : `${mm(r.width_mm)} ${mm(r.height_mm)}`;
}
/** Millimetres in twentieths of a point, Word's unit for a page. */
export function twips(millimetres) {
    return Math.round((millimetres * 1440) / 25.4);
}
/** The formats the paper choice applies to. */
export const PAPER_FORMATS = ['pdf', 'docx'];
export function choosesPaper(format) {
    return PAPER_FORMATS.includes(format);
}
/** The sheets offered at print time, in order: «the screen's size» only for a custom declaration. */
export function printPaperSizes(declared) {
    const named = ['a4', 'a3', 'a5', 'letter'];
    const ok = paperProblems(declared).length === 0;
    return ok && declared?.size === 'custom' ? [...named, 'screen'] : named;
}
/** Where the choice starts: the screen's declaration (a custom sheet is «the screen's size»), else A4, automatic. */
export function printPaperStart(declared) {
    const ok = paperProblems(declared).length === 0;
    const p = ok && declared ? declared : {};
    return { size: p.size === 'custom' ? 'screen' : (p.size ?? 'a4'), orientation: p.orientation ?? 'auto' };
}
/** The paper the file is laid out on for a choice: the screen's custom sides for `screen`, turned as chosen. */
export function paperForPrint(declared, choice) {
    if (choice.size === 'screen') {
        const ok = paperProblems(declared).length === 0 && declared?.size === 'custom';
        return ok ? { ...declared, orientation: choice.orientation } : { size: 'a4', orientation: choice.orientation };
    }
    return { size: choice.size, orientation: choice.orientation };
}
//# sourceMappingURL=paper.js.map